import { useMemo, useState } from "react";
import { Paper, Typography } from "@mui/material";
import { ApartmentOutlined, LocalShippingOutlined, PaymentsOutlined } from "@mui/icons-material";
import { Button, ChoiceToggle, OptionCardGroup, PageHeader, SectionHeader } from "@components/ui_v2";
import { CardResumenCotizacion, SliderCotizador } from "@components/ui_v2/cotizadoresComponents";
import { formatCurrencyShort } from "@helpers/funciones";
import {
  FLUJOS_PYMIO,
  FlujoPymioId,
  PLAZOS_FACTURAS_PYMIO,
  PLAZOS_MESES_PYMIO,
  PRODUCTOS_PYMIO,
  ProductoPymioId,
  TASA_ARRENDAMIENTO_PYMIO,
  TASA_CREDITO_PYMIO,
  armarCotizacionPymio,
} from "./cotizadorPymioConfig";

type TipoGarantia = "inmobiliaria" | "prendaria";

const FLUJO_ICONS: Record<FlujoPymioId, JSX.Element> = {
  liquidez: <PaymentsOutlined fontSize="small" />,
  activo: <LocalShippingOutlined fontSize="small" />,
};

const fmt = (amount: number) => formatCurrencyShort(amount, true);

/**
 * Cotizador de Pymio (`S_Cotizador` de `pymio/onboarding-screens.jsx`). Una sola solicitud puede pedir varios
 * productos: se elige la necesidad (liquidez o un activo) y los productos, y el cotizador se arma con el rango
 * que los cubre a todos (`armarCotizacionPymio`).
 *
 * TODO: solo diseño, con los datos mock del prototipo (`cotizadorPymioConfig`). Falta conectar la config del tax
 * system y la navegación a "Crea tu cuenta". Ya está registrado en `cotizadoresV2PorTipo` (`market_place`).
 */
const CotizadorPymio = () => {
  const [flujoId, setFlujoId] = useState<FlujoPymioId>("liquidez");
  // Sin selección previa se piden todos los productos del flujo, como el prototipo.
  const [seleccion, setSeleccion] = useState<ProductoPymioId[]>(FLUJOS_PYMIO.liquidez.productos);
  const cotizacion = useMemo(() => armarCotizacionPymio(flujoId, seleccion), [flujoId, seleccion]);

  const [montoElegido, setMonto] = useState<number>(cotizacion.monto);
  const [plazoMeses, setPlazoMeses] = useState<number>(24);
  const [plazoFacturas, setPlazoFacturas] = useState<number>(60);
  const [residual, setResidual] = useState<number>(20);
  const [garantia, setGarantia] = useState<TipoGarantia>("inmobiliaria");

  const { modo, conArrendamiento } = cotizacion;
  const esFactoraje = modo === "factoraje";
  // El rango cambia con los productos: el monto elegido se ajusta a él.
  const monto = Math.min(Math.max(montoElegido, cotizacion.min), cotizacion.max);

  // Crédito: cuota mensual amortizable. Factoraje: costo de fondeo del anticipo por los días de las facturas.
  // Arrendamiento: renta mensual sobre el valor menos el residual, que se liquida al final.
  const tasaAnual = conArrendamiento ? TASA_ARRENDAMIENTO_PYMIO : TASA_CREDITO_PYMIO;
  const montoResidual = conArrendamiento ? monto * (residual / 100) : 0;
  const tasaMensual = tasaAnual / 12;
  const factor = Math.pow(1 + tasaMensual, plazoMeses);
  const cuota = esFactoraje
    ? monto * tasaAnual * (plazoFacturas / 360)
    : ((monto - montoResidual) * (tasaMensual * factor)) / (factor - 1);

  const cambiarFlujo = (id: string) => {
    const nuevo = id as FlujoPymioId;
    setFlujoId(nuevo);
    setSeleccion(FLUJOS_PYMIO[nuevo].productos);
  };

  const opcionesProductos = FLUJOS_PYMIO[flujoId].productos.map((id) => ({
    value: id,
    label: PRODUCTOS_PYMIO[id].corto,
    title: PRODUCTOS_PYMIO[id].monto,
    testId: `cotizador-pymio-producto-${id}`,
  }));

  const plazoLabel = esFactoraje
    ? "Plazo de las facturas"
    : conArrendamiento
      ? "Plazo del arrendamiento"
      : modo === "tpv"
        ? "Plazo estimado de pago"
        : "Plazo";

  const handleSolicitar = () => {
    // TODO: navegar a "Crea tu cuenta" con la cotización cuando se conecte la config del tax system.
  };

  return (
    <>
      <PageHeader
        variant="cotizador"
        eyebrow={cotizacion.eyebrow}
        title={cotizacion.titulo}
        subtitle={cotizacion.subtitulo}
      />

      <Paper variant="lendia_paper">
        <SectionHeader
          variant="cotizador"
          label="¿Qué buscas para tu empresa?"
          meta="Define los productos que aplican"
          first
        />
        <OptionCardGroup
          layout="grid"
          columns={2}
          value={flujoId}
          onChange={cambiarFlujo}
          options={(Object.keys(FLUJOS_PYMIO) as FlujoPymioId[]).map((id) => ({
            value: id,
            label: FLUJOS_PYMIO[id].pregunta,
            sub: FLUJOS_PYMIO[id].resumen,
            icon: FLUJO_ICONS[id],
            testId: `cotizador-pymio-flujo-${id}`,
          }))}
        />

        <SectionHeader variant="cotizador" label="Productos de tu solicitud" meta="Puedes pedir varios a la vez" />
        <OptionCardGroup
          layout="check"
          columns={opcionesProductos.length}
          value={seleccion}
          onChange={(value) => setSeleccion(value as ProductoPymioId[])}
          options={opcionesProductos}
        />
        <Typography variant="body2" color="tokens.textTertiary" component="p" sx={{ mt: 1.25 }}>
          Cada financiera te hará una oferta por los productos que apliquen a tu empresa.
        </Typography>

        <SectionHeader
          variant="cotizador"
          label={cotizacion.montoLabel}
          meta={`${fmt(cotizacion.min)} – ${fmt(cotizacion.max)} MXN`}
        />
        <SliderCotizador
          value={monto}
          onChange={setMonto}
          min={cotizacion.min}
          max={cotizacion.max}
          step={cotizacion.step}
          valueText={fmt(monto)}
          unit="MXN"
          minLabel={fmt(cotizacion.min)}
          maxLabel={fmt(cotizacion.max)}
          loading={false}
          testId="cotizador-pymio-monto-slider"
        />

        <SectionHeader variant="cotizador" label={plazoLabel} />
        {esFactoraje ? (
          <ChoiceToggle
            variant="cotizador"
            value={plazoFacturas}
            onChange={(value) => setPlazoFacturas(Number(value))}
            options={PLAZOS_FACTURAS_PYMIO.map((dias) => ({ value: dias, label: `${dias} días` }))}
            testId="cotizador-pymio-plazo-facturas"
          />
        ) : (
          <ChoiceToggle
            variant="cotizador"
            value={plazoMeses}
            onChange={(value) => setPlazoMeses(Number(value))}
            options={PLAZOS_MESES_PYMIO.map((meses) => ({ value: meses, label: `${meses} meses` }))}
            testId="cotizador-pymio-plazo"
          />
        )}

        {cotizacion.conFactoraje && (
          <>
            <SectionHeader variant="cotizador" label="Plazo de las facturas" meta="Para tu línea de factoraje" />
            <ChoiceToggle
              variant="cotizador"
              value={plazoFacturas}
              onChange={(value) => setPlazoFacturas(Number(value))}
              options={PLAZOS_FACTURAS_PYMIO.map((dias) => ({ value: dias, label: `${dias} días` }))}
              testId="cotizador-pymio-plazo-facturas"
            />
            <Typography variant="body2" color="tokens.textTertiary" component="p" sx={{ mt: 1.25 }}>
              Días de crédito que das a tus clientes en las facturas que quieres anticipar.
            </Typography>
          </>
        )}

        {conArrendamiento && (
          <>
            <SectionHeader
              variant="cotizador"
              label="Valor residual"
              meta={
                residual === 0
                  ? "Arrendamiento puro · sin opción a compra"
                  : `${fmt(montoResidual)} al final · opción a compra`
              }
            />
            <SliderCotizador
              value={residual}
              onChange={setResidual}
              min={0}
              max={50}
              step={5}
              valueText={String(residual)}
              unit="%"
              minLabel="0% · puro"
              maxLabel="50%"
              loading={false}
              testId="cotizador-pymio-residual-slider"
            />
            <Typography variant="body2" color="tokens.textTertiary" component="p" sx={{ mt: 1.25 }}>
              {residual === 0
                ? "En arrendamiento puro rentas el activo por el plazo y lo devuelves al final; la renta es más alta porque amortiza todo el valor."
                : "Al final del contrato puedes ejercer la opción a compra por este monto o devolver el activo."}
            </Typography>
          </>
        )}

        {cotizacion.conGarantia && (
          <>
            <SectionHeader variant="cotizador" label="Tipo de garantía" meta="Para el crédito con garantía" />
            <OptionCardGroup
              layout="grid"
              columns={2}
              value={garantia}
              onChange={(value) => setGarantia(value as TipoGarantia)}
              options={[
                {
                  value: "inmobiliaria",
                  label: "Inmobiliaria",
                  sub: "Hipoteca sobre un inmueble",
                  icon: <ApartmentOutlined fontSize="small" />,
                  testId: "cotizador-pymio-garantia-inmobiliaria",
                },
                {
                  value: "prendaria",
                  label: "Prendaria",
                  sub: "Prenda sobre un bien mueble",
                  icon: <PaymentsOutlined fontSize="small" />,
                  testId: "cotizador-pymio-garantia-prendaria",
                },
              ]}
            />
            <Typography variant="body2" color="tokens.textTertiary" component="p" sx={{ mt: 1.25 }}>
              Registrarás los datos del bien al final de tu solicitud.
            </Typography>
          </>
        )}
      </Paper>

      <CardResumenCotizacion
        label={
          esFactoraje
            ? "Costo estimado por anticipo"
            : conArrendamiento
              ? "Renta mensual estimada"
              : "Pago mensual estimado"
        }
        amount={fmt(cuota)}
        caption={
          esFactoraje
            ? `MXN por anticipar ${fmt(monto)} a ${plazoFacturas} días · línea revolvente`
            : conArrendamiento
              ? `MXN / mes durante ${plazoMeses} meses · ${residual === 0 ? "arrendamiento puro" : `residual ${fmt(montoResidual)}`}`
              : `MXN / mes durante ${plazoMeses} meses`
        }
        disclaimer={
          esFactoraje
            ? "Estimación inicial con fines informativos. La oferta final está sujeta a aprobación y al análisis de las facturas y sus deudores."
            : "Estimación inicial con fines informativos. La oferta final está sujeta a aprobación y puede variar según el análisis crediticio, perfil y condiciones aplicables."
        }
        actions={
          <Button tone="hero" withArrow onClick={handleSolicitar} data-testid="cotizador-pymio-continue-button">
            Solicitar financiamiento
          </Button>
        }
      >
        {conArrendamiento && (
          <Typography variant="body2" color="tokens.textSecondary" component="p" sx={{ mt: 1.25 }}>
            Estimado con una tasa de {Math.round(TASA_ARRENDAMIENTO_PYMIO * 100)}% anual, el promedio de las financieras
            del marketplace. Esta es una solicitud: cada financiera te hará su propia oferta.
          </Typography>
        )}
      </CardResumenCotizacion>
    </>
  );
};

export default CotizadorPymio;
